<template>
  <div class="space-y-8">
    <RunnerPageHeading :read-only="!organizations.canManageRunners" :plan-required="planRequired" />

    <PlanRequiredNotice
      v-if="planRequired && organizations.canManageRunners"
      :recheck="refreshSubscription"
    >
      Running runners keep running and can be paused or deleted; creating, duplicating, editing and
      resuming runners are not available.
    </PlanRequiredNotice>

    <div
      v-else-if="monitoringPaused"
      class="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
      role="status"
      data-testid="monitoring-paused-notice"
    >
      <ExclamationTriangleIcon class="mt-0.5 size-5 shrink-0" aria-hidden="true" />
      <p v-if="organizations.canManageBilling">
        Metrics paused — check your
        <RouterLink :to="{ name: 'Billing' }" class="font-medium underline underline-offset-2"
          >subscription</RouterLink
        >.
      </p>
      <p v-else>
        Metrics paused — this organization's plan is inactive. Ask the organization owner to renew
        it.
      </p>
    </div>

    <div
      v-if="!loading && (runners.length > 0 || externalRunners.length > 0)"
      class="flex flex-wrap items-center justify-between gap-3"
    >
      <!-- Filter bar -->
      <RunnerFilterBar
        :selected-statuses="selectedStatuses"
        :selected-executors="selectedExecutors"
        :selected-locations="selectedLocations"
        :selected-platforms="selectedPlatforms"
        :search-query="searchQuery"
        :status-options="statusOptions"
        :executor-options="executorOptions"
        :location-options="locationOptions"
        :platform-options="platformOptions"
        @update:selected-statuses="onStatusFilterChange"
        @update:selected-executors="onExecutorFilterChange"
        @update:selected-locations="onLocationFilterChange"
        @update:selected-platforms="onPlatformFilterChange"
        @update:search-query="searchQuery = $event"
      />
      <MonitoringRangeSelect
        v-if="monitoringActive && monitoringRanges.length > 0"
        :model-value="metricsRange"
        :options="monitoringRanges"
        @update:model-value="metrics.select"
      />
    </div>

    <div
      class="grid grid-cols-1 gap-6 md:grid-cols-2 md:grid-flow-dense lg:grid-cols-2"
      data-testid="runner-grid"
    >
      <div
        v-if="externalError"
        class="col-span-full flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
        role="alert"
        data-testid="external-runners-error"
      >
        <ExclamationTriangleIcon class="mt-0.5 size-5 shrink-0" aria-hidden="true" />
        <p v-if="externalLoadedAt === null">
          External runners could not be loaded: {{ externalError }}
        </p>
        <p v-else>
          External runners could not be refreshed: {{ externalError }} Their status is as of
          {{ formatTime(externalLoadedAt) }}.
        </p>
      </div>

      <template
        v-for="item in gridItems"
        :key="`${item.kind}:${item.kind === 'managed' ? item.runner.id : item.runner.runnerId}`"
      >
        <RunnerCard
          v-if="item.kind === 'managed'"
          :name="item.runner.name"
          :specs="item.runner.specs"
          :concurrency="item.runner.concurrency"
          :executor="item.runner.executor"
          :is-paused="item.runner.isPaused"
          :isPauseButtonDisabled="item.runner.isPauseButtonDisabled"
          :isDuplicateButtonDisabled="item.runner.isDuplicateButtonDisabled"
          :isDeleteButtonDisabled="item.runner.isDeleteButtonDisabled"
          :duplicatable="item.runner.duplicatable"
          :status="item.runner.status"
          :ipv4="item.runner.ipv4"
          :ipv6="item.runner.ipv6"
          :country-code="item.runner.countryCode"
          :city="item.runner.city"
          :error-message="item.runner.errorMessage"
          :gitlab-host="item.runner.gitlabHost"
          :show-gitlab-instance="showGitlabInstance"
          :search-query="searchQuery"
          :monthly-price="item.runner.monthlyPrice"
          :cache-name="item.runner.cacheName"
          :monitoring="monitoringFor(item.runner)"
          :wide="showsMetrics(item.runner)"
          :metrics-connection-name="item.runner.metricsConnectionName"
          :metrics-status="item.runner.metricsStatus"
          :watchdog="item.runner.watchdog"
          :read-only="!organizations.canManageRunners"
          :plan-required="planRequired"
          :platform="item.runner.platform"
          :github="item.runner.github"
          :show-platform="platformOptions.length > 1"
          :provider="item.runner.provider"
          :burst-of="item.runner.burst ? (item.runner.burst.anchorName ?? '') : null"
          :burst-times="item.runner.burst?.times ?? null"
          size="lg"
          @resume="onResume(item.runner)"
          @pause="onPause(item.runner, $event)"
          @delete="onDelete(item.runner)"
          @force-delete="onForceDelete(item.runner)"
          @edit="onEdit(item.runner)"
          @duplicate="onDuplicate(item.runner)"
          @fix="onFix(item.runner, $event)"
          @fix-github="onFixGithub(item.runner, $event)"
          @dismiss-error="clearRunnerError(item.runner.id)"
        >
          <template v-if="!item.runner.burst" #schedule>
            <RunnerScheduleSection
              v-if="schedules.stateFor(item.runner.id)"
              :runner-id="item.runner.id"
              :runner-name="item.runner.name"
              :state="schedules.stateFor(item.runner.id)!"
              :can-edit="organizations.canManageRunners"
              :now="schedules.now.value"
              @change="schedules.set(item.runner.id, $event)"
              @retry="schedules.reload(item.runner.id)"
            />
            <RunnerAutoscalingSection
              v-if="scaling.stateFor(managedAnchor(item.runner))"
              :anchor="managedAnchor(item.runner)"
              :state="scaling.stateFor(managedAnchor(item.runner))!"
              :can-edit="organizations.canManageRunners && !planRequired"
              @change="onScalingChange(managedAnchor(item.runner), $event)"
              @retry="scaling.reload(managedAnchor(item.runner))"
            />
          </template>
        </RunnerCard>

        <ExternalRunnerCard
          v-else
          :runner="item.runner"
          :monitoring="externalMonitoring(item.runner)"
          :wide="showsExternalMetrics(item.runner)"
          :read-only="!organizations.canManageRunners"
          :plan-required="planRequired"
          :search-query="searchQuery"
          :now="clock"
          :stale-since="externalError ? externalLoadedAt : null"
          @install="installTarget = item.runner"
          @edit="router.push({ name: 'EditExternalRunner', params: { id: item.runner.runnerId } })"
          @delete="deleteTarget = item.runner"
        >
          <template v-if="scaling.stateFor(externalAnchor(item.runner))" #autoscaling>
            <RunnerAutoscalingSection
              :anchor="externalAnchor(item.runner)"
              :state="scaling.stateFor(externalAnchor(item.runner))!"
              :can-edit="organizations.canManageRunners && !planRequired"
              @change="onScalingChange(externalAnchor(item.runner), $event)"
              @retry="scaling.reload(externalAnchor(item.runner))"
            />
          </template>
        </ExternalRunnerCard>
      </template>

      <!-- Empty state / placeholders -->
      <template v-if="!loading && runners.length === 0 && externalRunners.length === 0">
        <div class="col-span-full text-sm text-gray-500 dark:text-gray-400">No runners found.</div>
      </template>
      <template v-else-if="!loading && nothingMatches">
        <div class="col-span-full text-sm text-gray-500 dark:text-gray-400">
          No runners match the current filters.
        </div>
      </template>
      <template v-if="loading">
        <div
          v-for="i in 3"
          :key="`skeleton-${i}`"
          class="rounded-xl border border-gray-200 bg-white p-5 dark:border-white/10 dark:bg-gray-900/60 animate-pulse"
        >
          <div class="h-4 w-1/3 rounded bg-gray-200 dark:bg-gray-700" />
          <div class="mt-2 h-3 w-1/2 rounded bg-gray-200 dark:bg-gray-700" />
          <!-- Match card details: 2 values per line on big screens -->
          <div class="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-2">
            <div v-for="j in 4" :key="j" class="h-5 rounded bg-gray-200 dark:bg-gray-700" />
          </div>
        </div>
      </template>
      <div v-if="error" class="col-span-full text-sm text-red-600 dark:text-red-400">
        {{ error }}
      </div>
    </div>
  </div>
  <InstallCommandDialog
    v-if="installTarget"
    :open="!!installTarget"
    :runner-id="installTarget.runnerId"
    :runner-name="installTarget.name"
    @close="onInstallClosed"
  />
  <ConfirmDialog
    :open="!!deleteTarget"
    :title="`Delete external runner ${deleteTarget?.name ?? ''}?`"
    :message="`Its agent stops reporting and its history is no longer shown. The server and its runner are not changed; to remove the agent, run ${UNINSTALL_COMMAND} in the folder of the install script on the server.`"
    confirm-label="Delete"
    busy-label="Deleting…"
    :busy="deleting"
    :error="deleteError"
    @confirm="onDeleteExternal"
    @cancel="deleteTarget = null"
  />
  <div class="fixed bottom-5 right-5 z-50 w-full max-w-sm">
    <SuccessNotification
      :show="showNotification"
      :message="notificationMessage"
      @dismiss="showNotification = false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { type LocationQuery, useRoute, useRouter } from 'vue-router';
import RunnerPageHeading from '@/components/RunnerPageHeading.vue';
import RunnerCard from '@/components/RunnerCard.vue';
import RunnerScheduleSection from '@/components/RunnerScheduleSection.vue';
import RunnerAutoscalingSection from '@/components/RunnerAutoscalingSection.vue';
import RunnerFilterBar from '@/components/RunnerFilterBar.vue';
import SuccessNotification from '@/components/SuccessNotification.vue';
import MonitoringRangeSelect from '@/components/MonitoringRangeSelect.vue';
import PlanRequiredNotice from '@/components/PlanRequiredNotice.vue';
import ExternalRunnerCard from '@/components/ExternalRunnerCard.vue';
import InstallCommandDialog from '@/components/InstallCommandDialog.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import {
  deleteExternalRunner,
  type ExternalRunnerResponse,
  listExternalRunners,
} from '@/api/external-runners';
import { UNINSTALL_COMMAND } from '@/lib/external-runners';
import { isSubscriptionRequired, planRequiredMessage } from '@/lib/subscription-required';
import { useSubscriptionStore } from '@/stores/subscription';
import { usePlanRequired } from '@/lib/entitlement';
import { ExclamationTriangleIcon } from '@heroicons/vue/20/solid';
import { type RunnerStatus, useRunnerFilters } from '@/lib/runner-filters';
import {
  loadMonitoringRange,
  type RunnerMonitoringView,
  useRunnerMetrics,
} from '@/lib/runner-metrics';
import { countDistinctGitlabInstances } from '@/lib/gitlab-instance';
import { useRunnerSchedules } from '@/lib/runner-schedules';
import { useRunnerScaling } from '@/lib/runner-scaling';
import { burstTimes, groupByAnchor, type BurstTimes } from '@/lib/scaling';
import type { ScalingAnchor, ScalingPolicy } from '@/api/scaling';
import { useOrganizationStore } from '@/stores/organization';
import {
  deleteRunner,
  duplicateRunner,
  forceDeleteRunner,
  getRunner,
  listRunners,
  type MonthlyPrice,
  type GithubRunnerScope,
  pauseRunner,
  resumeRunner,
  runnerProblem,
  type RunnerPlatform,
  type RunnerResponse,
  updateRunner,
} from '@/api/runners';
import { runnerPlatform } from '@/lib/platforms';
import { cloudProvider, type CloudProvider } from '@/lib/providers';

interface RunnerUI {
  id: string;
  name: string;
  productId: string;
  concurrency: number | null;
  executor: string | null;
  isPaused: boolean;
  isPauseButtonDisabled: boolean;
  isDuplicateButtonDisabled: boolean;
  isDeleteButtonDisabled: boolean;
  /** False when Hetzner reports this server type unavailable in the runner location. */
  duplicatable: boolean;
  countryCode: string;
  city?: string;
  status: RunnerStatus | 'error';
  ipv4?: string;
  ipv6?: string;
  specs: {
    chip: string;
    vcpu: number | string;
    ram: string;
    nvmeSsd: string | number;
  };
  errorMessage?: string;
  errorTimestamp?: number;
  gitlabHost?: string;
  monthlyPrice?: MonthlyPrice | null;
  monitoringEnabled: boolean;
  metricsConnectionName: string | null;
  metricsStatus: RunnerResponse['metricsStatus'];
  watchdog: RunnerResponse['watchdog'];
  cacheName?: string | null;
  platform: RunnerPlatform;
  github: GithubRunnerScope | null;
  provider: CloudProvider;
  /** Set for a burst runner of a scaling policy. */
  burst: { anchorRunnerId: string; anchorName: string | null; times: BurstTimes } | null;
}
const organizations = useOrganizationStore();
const subscription = useSubscriptionStore();
const { planRequired, refresh: refreshSubscription } = usePlanRequired();
// Reactive state for the notification
const showNotification = ref(false);
const notificationMessage = ref('');
const loading = ref(true);
const error = ref<string | null>(null);
const runners = ref<RunnerUI[]>([]);

// ---- Filter state (composable, persisted to localStorage) ----
const {
  selectedStatuses,
  selectedExecutors,
  selectedLocations,
  selectedPlatforms,
  searchQuery,
  statusOptions,
  executorOptions,
  locationOptions,
  platformOptions,
  filteredRunners,
  save: saveFiltersToStorage,
  pruneSelected: pruneSelectedFilters,
} = useRunnerFilters(runners);

// Filtered + sorted runners (apply sorting on top of filtered results)
const filteredSortedRunners = computed(() =>
  [...filteredRunners.value].sort((a, b) => a.name.localeCompare(b.name))
);

// Show the connected GitLab instance on cards only when the user's runners span
// more than one distinct instance. This is account-wide: it deliberately uses the
// complete runners collection (including paused/unknown runners), not the filtered
// list, so search/filtering never makes the field disappear.
const showGitlabInstance = computed(
  () => countDistinctGitlabInstances(runners.value.map((r) => r.gitlabHost)) > 1
);

function onStatusFilterChange(value: string[]) {
  selectedStatuses.value = value;
  saveFiltersToStorage();
}
function onExecutorFilterChange(value: string[]) {
  selectedExecutors.value = value;
  saveFiltersToStorage();
}
function onLocationFilterChange(value: string[]) {
  selectedLocations.value = value;
  saveFiltersToStorage();
}
function onPlatformFilterChange(value: string[]) {
  selectedPlatforms.value = value;
  saveFiltersToStorage();
}
const schedules = useRunnerSchedules(() => runners.value.filter((r) => !r.burst).map((r) => r.id));

// ---- External runners: self-hosted servers that only report metrics ----
const externalRunners = ref<ExternalRunnerResponse[]>([]);
const externalError = ref<string | null>(null);
const externalLoadedAt = ref<number | null>(null);
const clock = ref(Date.now());
const installTarget = ref<ExternalRunnerResponse | null>(null);
const deleteTarget = ref<ExternalRunnerResponse | null>(null);
const deleting = ref(false);
const deleteError = ref<string | null>(null);
// Their status follows the agent's reports, which arrive every minute.
const EXTERNAL_POLL_MS = 60_000;
let externalTimer: number | null = null;

// The managed runners' status, executor and location filters do not apply to them, so they are hidden while one is set.
const filteredExternalRunners = computed(() => {
  if (
    selectedStatuses.value.length ||
    selectedExecutors.value.length ||
    selectedLocations.value.length
  ) {
    return [];
  }
  const platforms = new Set(selectedPlatforms.value);
  const query = searchQuery.value.trim().toLowerCase();
  return [...externalRunners.value]
    .filter((r) => platforms.size === 0 || platforms.has(r.platform))
    .filter(
      (r) =>
        !query ||
        [r.name, r.platform, 'external', ...r.labels].some((value) =>
          value.toLowerCase().includes(query)
        )
    )
    .sort((a, b) => a.name.localeCompare(b.name));
});

const gridItems = computed(() =>
  groupByAnchor(filteredSortedRunners.value, filteredExternalRunners.value)
);

function managedAnchor(runner: RunnerUI): ScalingAnchor {
  return { kind: 'MANAGED', runnerId: runner.id };
}

function externalAnchor(runner: ExternalRunnerResponse): ScalingAnchor {
  return { kind: 'EXTERNAL', runnerId: runner.runnerId };
}

const scaling = useRunnerScaling(() => [
  ...runners.value.filter((r) => !r.burst).map(managedAnchor),
  ...externalRunners.value.map(externalAnchor),
]);

function onScalingChange(anchor: ScalingAnchor, policy: ScalingPolicy | null) {
  scaling.set(anchor, policy);
  // Burst runners come and go with the policy.
  void fetchRunners();
}

const nothingMatches = computed(
  () => filteredSortedRunners.value.length === 0 && filteredExternalRunners.value.length === 0
);

async function fetchExternalRunners() {
  try {
    const data = await listExternalRunners();
    if (disposed) return;
    externalRunners.value = data;
    externalLoadedAt.value = Date.now();
    externalError.value = null;
  } catch (e: unknown) {
    if (disposed) return;
    // Managed runners stay usable, and the next poll tries again. The banner continues after the message,
    // which can be a bare client error such as "Network Error".
    const message = mutationErrorMessage(e, 'The request failed.');
    externalError.value = /[.!?]$/.test(message) ? message : `${message}.`;
  } finally {
    clock.value = Date.now();
  }
}

function formatTime(at: number): string {
  return new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function showsExternalMetrics(runner: ExternalRunnerResponse): boolean {
  return runner.monitoringEnabled && runner.status !== 'WAITING_FOR_INSTALL';
}

function externalMonitoring(runner: ExternalRunnerResponse): RunnerMonitoringView | null {
  return showsExternalMetrics(runner) ? metrics.viewFor(runner.runnerId) : null;
}

function onInstallClosed() {
  installTarget.value = null;
  void fetchExternalRunners();
}

async function onDeleteExternal() {
  const target = deleteTarget.value;
  if (!target) return;
  deleting.value = true;
  deleteError.value = null;
  try {
    await deleteExternalRunner(target.runnerId);
    if (disposed) return;
    externalRunners.value = externalRunners.value.filter((r) => r.runnerId !== target.runnerId);
    deleteTarget.value = null;
    triggerNotification(
      `External runner ${target.name} deleted. Run ${UNINSTALL_COMMAND} on the server to remove the agent.`
    );
  } catch (e: unknown) {
    deleteError.value = mutationErrorMessage(e, 'Failed to delete the external runner');
  } finally {
    deleting.value = false;
  }
}

// ---- Dashboard metrics (separate from the runner state polling below) ----
const metricsRange = ref(loadMonitoringRange());
const monitoringActive = computed(
  () => runners.value.some(showsMetrics) || externalRunners.value.some(showsExternalMetrics)
);
const metrics = useRunnerMetrics(monitoringActive, metricsRange);
const monitoringPaused = computed(() => monitoringActive.value && metrics.subscriptionPaused.value);
const monitoringRanges = metrics.availableRanges;

// Independent of Metrics data, so a card keeps its width while Metrics load or poll.
function showsMetrics(runner: RunnerUI): boolean {
  return runner.status === 'active' && runner.monitoringEnabled;
}

function monitoringFor(runner: RunnerUI): RunnerMonitoringView | null {
  return showsMetrics(runner) ? metrics.viewFor(runner.id) : null;
}

// Polling configuration: override via VITE_RUNNERS_POLL_MS
const pollIntervalMs = Number(import.meta.env?.VITE_RUNNERS_POLL_MS ?? 3000) || 3000;
let pollTimer: number | null = null;
let inFlight = false; // prevent overlapping fetches during polling
// The view is remounted per organization; late completions of a disposed instance must not act.
let disposed = false;
const hasTransitionalRunners = computed(() => {
  return runners.value.some(
    (r) =>
      r.status !== 'active' &&
      r.status !== 'paused' &&
      r.status !== 'unknown' &&
      r.status !== 'registration_failed'
  );
});
function deriveStatus(state: string | undefined | null): RunnerStatus | 'error' {
  const s = (state || '').toUpperCase();
  switch (s) {
    // Active states
    case 'RUNNING':
    case 'ACTIVE':
    case 'READY':
      return 'active';

    // Offline states (paused counts as offline)
    case 'PAUSED':
    case 'OFFLINE':
    case 'STOPPED':
      return 'paused';
    case 'CONFIGURING':
      return 'configuring';
    case 'ERROR':
      return 'error';
    case 'PAUSING':
      return 'pausing';
    case 'UNKNOWN':
      return 'unknown';
    case 'INVALID_GITLAB_TOKEN':
      return 'invalid_gitlab_token';
    // Terminal for GitHub runners: the VM could not install or register the runner.
    case 'REGISTRATION_FAILED':
      return 'registration_failed';
    // Everything else is initializing (creating/configuring/transitional/error, etc.)
    default:
      return 'creating';
  }
}

function mapRunner(r: RunnerResponse): RunnerUI {
  const stateUpper = (r.state || '').toUpperCase();
  const isPaused = stateUpper === 'PAUSED';
  const transitionalActionDisabled =
    stateUpper === 'PAUSING' || stateUpper === 'CREATING' || stateUpper === 'UNKNOWN';
  const isPauseButtonDisabled = stateUpper === 'CREATING' || stateUpper === 'UNKNOWN';
  const isDeleteButtonDisabled = stateUpper === 'CREATING';
  return {
    id: r.runnerId,
    name: r.name,
    // No productId in list response; show runnerId or gitlabHost as a surrogate
    isPauseButtonDisabled,
    isDuplicateButtonDisabled: transitionalActionDisabled,
    isDeleteButtonDisabled: isDeleteButtonDisabled,
    duplicatable: r.duplicatable,
    productId: r.productId,
    concurrency: r.concurrency,
    executor: r.executor || null,
    isPaused: isPaused,
    status: deriveStatus(r.state),
    countryCode: r.locationCountry,
    city: r.locationCity,
    ipv4: r.ipv4,
    ipv6: r.ipv6,
    specs: {
      chip: r.cpuArchitecture || '—',
      vcpu: r.cpus ?? '—',
      ram: r.memory != null ? `${r.memory} GB` : '—',
      nvmeSsd: r.disk != null ? `${r.disk} GB` : '—',
    },
    gitlabHost: r.gitlabHost ?? undefined,
    monthlyPrice: r.monthlyPrice ?? null,
    monitoringEnabled: r.monitoringEnabled === true,
    metricsConnectionName: r.metricsConnectionId
      ? (r.metricsConnectionName ?? r.metricsConnectionId)
      : null,
    metricsStatus: r.metricsStatus ?? null,
    watchdog: r.watchdog ?? null,
    cacheName: r.cacheName ?? null,
    platform: runnerPlatform(r),
    github: r.github ?? null,
    provider: cloudProvider(r.provider),
    burst: r.burst
      ? {
          anchorRunnerId: r.burst.anchorRunnerId,
          anchorName: r.burst.anchorName,
          times: burstTimes(r.state, r.burst.leaving ?? false, r.burst.created, r.burst.startedAt),
        }
      : null,
  };
}

async function fetchRunners() {
  // Mark inFlight for polling coordination but still allow manual actions to reuse
  if (inFlight) return; // avoid overlapping fetches
  inFlight = true;
  if (!loading.value) {
    // keep existing content visible; only show skeletons for initial load
  } else {
    error.value = null;
  }
  try {
    const data = await listRunners();
    if (disposed) return;
    // Keep card errors visible across refreshes; runner objects are replaced on every fetch.
    const previous = new Map(runners.value.map((r) => [r.id, r]));
    runners.value = data.map((r) => {
      const mapped = mapRunner(r);
      const prev = previous.get(mapped.id);
      if (prev?.errorMessage) {
        mapped.errorMessage = prev.errorMessage;
        mapped.errorTimestamp = prev.errorTimestamp;
      }
      return mapped;
    });
    pruneSelectedFilters();
    error.value = null;
    // After fetching, check if we need to start/stop polling based on runner states
    if (hasTransitionalRunners.value) {
      startPolling();
    } else {
      stopPolling();
    }
  } catch (failure) {
    if (disposed) return;
    const e = failure as { message?: string; response?: { data?: { message?: string } } } | null;
    // Keep current runners on transient errors; surface message once
    error.value = e?.response?.data?.message || e?.message || 'Failed to load runners';
  } finally {
    loading.value = false;
    inFlight = false;
  }
}

function triggerNotification(message: string) {
  notificationMessage.value = message;
  showNotification.value = true;

  // Optional: Automatically hide the notification after 5 seconds
  setTimeout(() => {
    showNotification.value = false;
  }, 5000);
}

// Handle one-time notifications via router query params (e.g., after registration)
const route = useRoute();
const router = useRouter();

function clearQueryParam(name: string) {
  const q: LocationQuery = { ...route.query };
  delete q[name];
  router.replace({ query: q });
}

onMounted(() => {
  // Normalize potential array value from query params
  const registeredParam = route.query.registered;
  const registered = Array.isArray(registeredParam) ? registeredParam[0] : registeredParam;
  if (registered === '1') {
    triggerNotification(
      'Your account was created. We emailed you a 6-digit code to verify your email address.'
    );
    clearQueryParam('registered');
  }
});

function startPolling() {
  if (pollTimer !== null || disposed) return;
  // Skip polling in background tabs; resume on visibilitychange
  if (document.hidden) return;
  pollTimer = window.setInterval(() => {
    if (!document.hidden) {
      void fetchRunners();
    }
  }, pollIntervalMs);
}

function stopPolling() {
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

function handleVisibilityChange() {
  if (document.hidden) {
    stopPolling();
  } else {
    // immediate refresh when returning to tab, then resume polling
    void fetchRunners();
    void fetchExternalRunners();
    startPolling();
  }
}
// Might be useful in the future
function handleWindowFocus() {
  // Extra guard: on focus, trigger a quick refresh
  // if (!document.hidden) {
  //   void fetchRunners();
  // }
}

function extractRunnerIdFromError(e: unknown): string | null {
  const instance = (e as { response?: { data?: { instance?: unknown } } } | null)?.response?.data
    ?.instance;
  if (!instance || typeof instance !== 'string') return null;
  // Extract runner ID from path like "/rest/runner/812999368881481087/resume"
  const match = instance.match(/\/runner\/(\d+)/);
  return match ? match[1] : null;
}

function mutationErrorMessage(e: unknown, fallback: string): string {
  if (isSubscriptionRequired(e)) {
    // The plan ended since it was loaded; reloading it shows the notice and disables the actions.
    void refreshSubscription();
    return planRequiredMessage(organizations.canManageBilling);
  }
  const { response, message } = (e ?? {}) as { response?: { data?: unknown }; message?: string };
  const data = response?.data;
  if (typeof data === 'string' && data) return data;
  const body = data as { detail?: string; message?: string } | undefined;
  return body?.detail || body?.message || message || fallback;
}

function setRunnerError(runnerId: string, errorMsg: string) {
  const runner = runners.value.find((r) => r.id === runnerId);
  if (runner) {
    runner.errorMessage = errorMsg;
    runner.errorTimestamp = Date.now();
    // Auto-dismiss after 60 seconds
    setTimeout(() => {
      const current = runners.value.find((r) => r.id === runnerId);
      if (current && current.errorMessage === errorMsg && current.errorTimestamp) {
        const age = Date.now() - current.errorTimestamp;
        if (age >= 60000) {
          current.errorMessage = undefined;
          current.errorTimestamp = undefined;
        }
      }
    }, 60000);
  }
}

function clearRunnerError(runnerId: string) {
  const runner = runners.value.find((r) => r.id === runnerId);
  if (runner) {
    runner.errorMessage = undefined;
    runner.errorTimestamp = undefined;
  }
}

async function onResume(runner: RunnerUI) {
  const originalPaused = runner.isPaused;
  const originalStatus = runner.status;
  runner.isPaused = false; // optimistic
  runner.isPauseButtonDisabled = true;
  runner.status = 'creating';
  // Clear any previous error
  clearRunnerError(runner.id);
  try {
    await resumeRunner(runner.id);
    // Reconcile with backend truth
    startPolling();
  } catch (e) {
    if (disposed) return;
    runner.isPaused = originalPaused;
    runner.status = originalStatus;
    const errorMsg = mutationErrorMessage(e, 'Failed to resume runner');

    // Try to extract runner ID from error, fall back to current runner
    const errorRunnerId = extractRunnerIdFromError(e) || runner.id;
    setRunnerError(errorRunnerId, errorMsg);
  }
}

async function onPause(runner: RunnerUI, options: { force?: boolean } = {}) {
  const originalPaused = runner.isPaused;
  const originalStatus = runner.status;
  runner.isPauseButtonDisabled = true;
  runner.status = 'pausing'; // optimistic
  try {
    const state = await pauseRunner(runner.id, options);
    if (disposed) return;
    runner.isPaused = state === 'PAUSED';
    runner.status = deriveStatus(state);
    runner.isPauseButtonDisabled = false;
    startPolling();
  } catch (e) {
    if (disposed) return;
    runner.isPaused = originalPaused;
    runner.status = originalStatus;
    setRunnerError(runner.id, mutationErrorMessage(e, 'Failed to pause runner'));
    void fetchRunners();
  }
}

async function onDuplicate(runner: RunnerUI) {
  runner.isDuplicateButtonDisabled = true;
  try {
    await duplicateRunner(runner.id);
    if (disposed) return;
    triggerNotification(`Runner ${runner.name} duplicated successfully.`);
    void fetchRunners();
    startPolling();
  } catch (e) {
    if (disposed) return;
    runner.isDuplicateButtonDisabled = false;
    setRunnerError(runner.id, mutationErrorMessage(e, 'Failed to duplicate runner'));
  }
}

function onEdit(runner: RunnerUI) {
  router.push({ name: 'EditRunner', params: { id: runner.id } });
}

async function onFix(runner: RunnerUI, payload: { runnerToken: string; gitlabHost: string }) {
  try {
    // We need the full runner data to perform a PUT update, but the UI only has a subset.
    // Fetch full data first.
    const fullRunner = await getRunner(runner.id);
    if (disposed) return;
    await updateRunner(runner.id, {
      name: fullRunner.name,
      productId: fullRunner.productId,
      gitlabHost: payload.gitlabHost,
      runnerToken: payload.runnerToken,
      concurrency: fullRunner.concurrency,
      executor: fullRunner.executor ?? undefined,
      sshKeyIds: fullRunner.sshKeyIds,
      labels: fullRunner.labels ?? [],
    });
    if (disposed) return;
    triggerNotification(`Runner ${runner.name} fix submitted successfully.`);
    fetchRunners();
  } catch (e) {
    if (disposed) return;
    setRunnerError(runner.id, mutationErrorMessage(e, `Failed to fix runner ${runner.name}`));
  }
}

async function onFixGithub(runner: RunnerUI, token: string) {
  clearRunnerError(runner.id);
  let step: GithubFixStep = 'update';
  let tokenSaved = false;
  try {
    if (token) {
      const fullRunner = await getRunner(runner.id);
      if (disposed) return;
      await updateRunner(runner.id, {
        name: fullRunner.name,
        productId: fullRunner.productId,
        concurrency: fullRunner.concurrency,
        sshKeyIds: fullRunner.sshKeyIds,
        labels: fullRunner.labels ?? [],
        github: { token },
      });
      if (disposed) return;
      tokenSaved = true;
    }
    // A new token is stored without recreating the VM, whose registration already failed. Only paused
    // runners can be resumed, so the VM is replaced by pausing and resuming; the new one registers again.
    step = 'pause';
    const state = await pauseRunner(runner.id, { force: true });
    if (disposed) return;
    if (state !== 'PAUSED') {
      step = 'wait';
      if (!(await waitUntilPaused(runner.id))) return;
    }
    step = 'resume';
    await resumeRunner(runner.id);
    if (disposed) return;
    triggerNotification(
      tokenSaved
        ? `Token of runner ${runner.name} replaced; its server is being recreated.`
        : `The server of runner ${runner.name} is being recreated.`
    );
    void fetchRunners();
    startPolling();
  } catch (e: unknown) {
    if (disposed) return;
    setRunnerError(runner.id, githubFixFailure(step, tokenSaved, e));
    void fetchRunners();
  }
}

type GithubFixStep = 'update' | 'pause' | 'wait' | 'resume';

// The server's deletion is confirmed by the backend's minute-by-minute check.
const PAUSE_CONFIRMATION_TIMEOUT_MS = 5 * 60 * 1000;

/** False if the dashboard was left meanwhile; throws if the runner is not paused in time. */
async function waitUntilPaused(runnerId: string): Promise<boolean> {
  const deadline = Date.now() + PAUSE_CONFIRMATION_TIMEOUT_MS;
  for (;;) {
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    if (disposed) return false;
    const state = (await getRunner(runnerId)).state.toUpperCase();
    if (disposed) return false;
    if (state === 'PAUSED') return true;
    if (state !== 'PAUSING') throw new Error(`The runner is in state ${state} instead of paused.`);
    if (Date.now() >= deadline) throw new Error('Its server is still being deleted.');
  }
}

const GITHUB_TOKEN_REFUSALS = new Set([
  'GITHUB_TOKEN_INVALID',
  'GITHUB_PERMISSION_DENIED',
  'GITHUB_NOT_FOUND',
  'RUNNER_PAUSED_TOKEN_REFUSED',
]);

function githubFixFailure(step: GithubFixStep, tokenSaved: boolean, e: unknown): string {
  const problem = runnerProblem(e, '');
  const refused = GITHUB_TOKEN_REFUSALS.has(problem.code ?? '');
  const reason =
    refused || problem.code === 'GITHUB_UNAVAILABLE'
      ? problem.message
      : mutationErrorMessage(e, 'The request failed.');
  const saved = tokenSaved ? 'The new token is saved. ' : '';
  if (step === 'update') {
    // The backend answers an update that changes nothing with 404, as it does for a deleted runner.
    return problem.status === 404
      ? 'Nothing was saved: the runner was not found, or it already uses this token. If the token is ' +
          'already saved, open Fix again and leave the token empty to recreate the server.'
      : reason;
  }
  if (step === 'pause') {
    return (
      `${saved}Pausing the runner to recreate its server failed, so the runner is unchanged: ${reason} ` +
      `Try again later with Fix${tokenSaved ? ' and leave the token empty' : ''}.`
    );
  }
  if (step === 'wait') {
    return `${saved}The server is being replaced, but the runner was not paused in time to resume it: ${reason} Use Resume once it is paused.`;
  }
  if (refused) {
    const detail = problem.code === 'RUNNER_PAUSED_TOKEN_REFUSED' ? '' : ` ${reason}`;
    return (
      `${saved}The runner is paused, because GitHub refused its token.${detail} Enter a working token ` +
      'under Edit, then resume the runner.'
    );
  }
  return `${saved}The runner is paused, but resuming it failed: ${reason} Use Resume to try again.`;
}

function rollbackFailedDelete(removed: RunnerUI, idx: number, e: unknown) {
  if (disposed) return;
  // 404 means the runner is already gone, so it stays hidden.
  if ((e as { response?: { status?: number } } | undefined)?.response?.status !== 404) {
    // A refresh during the request may already have brought the runner back with newer state.
    if (!runners.value.some((r) => r.id === removed.id)) {
      runners.value.splice(idx, 0, removed);
    }
    setRunnerError(removed.id, mutationErrorMessage(e, 'Failed to delete runner'));
  }
  void fetchRunners();
}

async function onDelete(runner: RunnerUI) {
  const idx = runners.value.findIndex((r) => r.id === runner.id);
  if (idx === -1) return;
  const removed = runners.value.splice(idx, 1)[0]!; // optimistic remove (non-null: idx validated)
  try {
    await deleteRunner(runner.id);
  } catch (e) {
    rollbackFailedDelete(removed, idx, e);
  }
}

async function onForceDelete(runner: RunnerUI) {
  const idx = runners.value.findIndex((r) => r.id === runner.id);
  if (idx === -1) return;
  const removed = runners.value.splice(idx, 1)[0]!; // optimistic remove (non-null: idx validated)
  try {
    await forceDeleteRunner(runner.id);
  } catch (e) {
    rollbackFailedDelete(removed, idx, e);
  }
}

onMounted(() => {
  if (subscription.organizationId !== organizations.selectedId) void refreshSubscription();
  fetchRunners();
  void fetchExternalRunners();
  externalTimer = window.setInterval(() => {
    if (!document.hidden) void fetchExternalRunners();
  }, EXTERNAL_POLL_MS);
  startPolling();
  metrics.start();
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('focus', handleWindowFocus);
});

onBeforeUnmount(() => {
  disposed = true;
  stopPolling();
  if (externalTimer !== null) clearInterval(externalTimer);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('focus', handleWindowFocus);
});
</script>
